import React from 'react';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';
import { Cookie, Settings, Shield, Info, ToggleRight } from 'lucide-react';
import { Card } from '../components/ui/card';

export default function CookiePolicy() {
  const cookieTypes = [
    {
      name: 'Essential Cookies',
      required: true,
      description: 'These cookies are necessary for the website to function and cannot be switched off. They are usually set in response to actions you take, such as logging in or filling in forms.',
      examples: ['Session ID', 'Authentication tokens', 'Security cookies', 'Load balancing']
    },
    {
      name: 'Functional Cookies',
      required: false,
      description: 'These cookies enable enhanced functionality and personalisation, such as remembering your preferences and settings.',
      examples: ['Language preferences', 'Theme settings', 'Previously viewed properties', 'Search filters']
    },
    {
      name: 'Analytics Cookies',
      required: false,
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      examples: ['Page views', 'Traffic sources', 'User journeys', 'Feature usage']
    },
    {
      name: 'Marketing Cookies',
      required: false,
      description: 'These cookies may be set through our site by advertising partners to build a profile of your interests.',
      examples: ['Remarketing pixels', 'Social media tracking', 'Conversion tracking']
    }
  ];

  return (
    <PublicLayout>
      <SEO 
        title="Cookie Policy | LetWise"
        description="Cookie Policy for LetWise. Information about how we use cookies and similar technologies on our website."
      />
      
      <div className="bg-slate-50 min-h-screen">
        {/* Header */}
        <div className="bg-primary text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Cookie className="w-16 h-16 mx-auto mb-6 opacity-80" />
              <h1 className="text-4xl font-bold mb-4">Cookie Policy</h1>
              <p className="text-xl opacity-90">
                C&C LET MANAGEMENT LIMITED trading as LetWise
              </p>
              <p className="text-sm mt-4 opacity-75">
                Last updated: January 2026
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Introduction */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Info className="w-6 h-6 text-primary" />
                What Are Cookies?
              </h2>
              <p className="text-slate-600 mb-4">
                Cookies are small text files that are placed on your device when you visit a website. 
                They are widely used to make websites work more efficiently and to provide information 
                to website owners.
              </p>
              <p className="text-slate-600">
                We use cookies and similar technologies (such as local storage) to:
              </p>
              <ul className="list-disc pl-6 mt-2 text-slate-600 space-y-1">
                <li>Keep you signed in to your account</li>
                <li>Remember your preferences</li>
                <li>Understand how you use our website</li>
                <li>Improve your experience</li>
              </ul>
            </section>

            {/* Cookie Types */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Settings className="w-6 h-6 text-primary" />
                Types of Cookies We Use
              </h2>
              <div className="space-y-6">
                {cookieTypes.map((cookie, idx) => (
                  <Card key={idx} className={`p-6 ${cookie.required ? 'border-l-4 border-l-primary' : ''}`}>
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-lg text-slate-900">{cookie.name}</h3>
                      {cookie.required ? (
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                          Always Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded flex items-center gap-1">
                          <ToggleRight className="w-3 h-3" />
                          Optional
                        </span>
                      )}
                    </div>
                    <p className="text-slate-600 mb-3">{cookie.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {cookie.examples.map((example, i) => (
                        <span key={i} className="px-2 py-1 bg-slate-50 text-slate-500 text-xs rounded">
                          {example}
                        </span>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            </section>

            {/* Third Party Cookies */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Third-Party Cookies</h2>
              <p className="text-slate-600 mb-4">
                Some cookies are placed by third-party services that appear on our pages. We use the following third-party services:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 font-semibold">Service</th>
                      <th className="text-left py-3 font-semibold">Purpose</th>
                      <th className="text-left py-3 font-semibold">More Information</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600">
                    <tr className="border-b">
                      <td className="py-3">Google Analytics</td>
                      <td className="py-3">Website analytics</td>
                      <td className="py-3"><a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3">Mapbox</td>
                      <td className="py-3">Property maps</td>
                      <td className="py-3"><a href="https://www.mapbox.com/legal/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3">reCAPTCHA</td>
                      <td className="py-3">Bot protection</td>
                      <td className="py-3"><a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Managing Cookies */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Shield className="w-6 h-6 text-primary" />
                Managing Your Cookie Preferences
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  You can control and manage cookies in various ways:
                </p>
                <h3 className="font-semibold text-slate-900 mt-4">Browser Settings</h3>
                <p>
                  Most browsers allow you to refuse cookies or delete existing cookies. However, if you 
                  block essential cookies, some parts of our website may not function properly.
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><a href="https://support.google.com/chrome/answer/95647" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Safari</a></li>
                  <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
                </ul>
                
                <h3 className="font-semibold text-slate-900 mt-6">Opt-Out Tools</h3>
                <p>
                  For analytics cookies, you can opt out using:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><a href="https://tools.google.com/dlpage/gaoptout" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
                </ul>
              </div>
            </section>

            {/* Changes */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Changes to This Policy</h2>
              <p className="text-slate-600">
                We may update this Cookie Policy from time to time. Any changes will be posted on this 
                page with an updated revision date. We encourage you to review this policy periodically.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-primary/5 border border-primary/20 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Contact Us</h2>
              <p className="text-slate-600 mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <p className="text-slate-600">
                <strong>C&C LET MANAGEMENT LIMITED</strong> trading as <strong>LetWise</strong><br />
                Unit 4, 416 Hamilton Road, Cambuslang, Glasgow, G72 7XR<br />
                Email: <a href="mailto:privacy@letwise.io" className="text-primary hover:underline">privacy@letwise.io</a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
